import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { GetEvaluationQuestions } from "../../../actions/teacherQueries";
import {
  PostEvaluationQuestions,
  UpdateEvaluationQuestion,
  DeleteEvaluationQuestion,
  errorMessage,
} from "../../../actions/teacherMutations";

export default function useAPLEvaluationQuestions(activePeriodId) {
  const [studentQuestions, setStudentQuestions] = useState([]);
  const [supervisorQuestions, setSupervisorQuestions] = useState([]);
  const [questionsToDelete, setQuestionsToDelete] = useState([]);
  const queryClient = useQueryClient();

  const createEvaluationQuestions = useMutation({ mutationFn: PostEvaluationQuestions });
  const updateEvaluationQuestion = useMutation({ mutationFn: UpdateEvaluationQuestion });
  const deleteEvaluationQuestions = useMutation({ mutationFn: DeleteEvaluationQuestion });

  const evaluationQuestionsData = GetEvaluationQuestions(activePeriodId);

  useEffect(() => {
    if (
      !evaluationQuestionsData.isPending &&
      evaluationQuestionsData.data !== null
    ) {
      const tempStudentQuestions = [];
      const tempSupervisorQuestions = [];

      evaluationQuestionsData.data.forEach((question) => {
        switch (question.forUserType) {
          case 4:
            tempSupervisorQuestions.push(question);
            break;
          case 1:
            tempStudentQuestions.push(question);
            break;
          default:
            break;
        }
      });

      setStudentQuestions(tempStudentQuestions);
      setSupervisorQuestions(tempSupervisorQuestions);
    }
  }, [evaluationQuestionsData.isPending, evaluationQuestionsData.data]);

  const handleSaveEvaluationQuestions = async (
    periodId,
    studentQs,
    supervisorQs,
  ) => {
    const newQuestions = [];
    const questionsToUpdate = [];

    studentQs.forEach((q, index) => {
      const question = {
        periodId: periodId,
        question: q.question,
        responseRange: q.responseRange,
        sortOrder: index + 1,
        forUserType: "Student",
      };
      if (q.id) {
        const original = studentQuestions.find((oq) => oq.id === q.id);
        if (
          !original ||
          original.question !== q.question ||
          original.responseRange !== q.responseRange
        ) {
          questionsToUpdate.push({
            id: q.id,
            question: question,
          });
        }
      } else {
        newQuestions.push(question);
      }
    });

    supervisorQs.forEach((q, index) => {
      const question = {
        periodId: periodId,
        question: q.question,
        responseRange: q.responseRange,
        sortOrder: index + 1,
        forUserType: "AplSupervisor",
      };
      if (q.id) {
        const original = supervisorQuestions.find((oq) => oq.id === q.id);
        if (
          !original ||
          original.question !== q.question ||
          original.responseRange !== q.responseRange
        ) {
          questionsToUpdate.push({
            id: q.id,
            question: question,
          });
        }
      } else {
        newQuestions.push(question);
      }
    });

    const promises = [];

    if (newQuestions.length > 0) {
      promises.push(
        createEvaluationQuestions
          .mutateAsync(newQuestions)
          .then((data) => {
            const tempStudentQuestions = [...studentQs];
            const tempSupervisorQuestions = [...supervisorQs];
            data.forEach((question) => {
              switch (question.forUserType) {
                case 4:
                  tempSupervisorQuestions.push(question);
                  break;
                case 1:
                  tempStudentQuestions.push(question);
                  break;
                default:
                  break;
              }
            });

            let tempQuestion = tempStudentQuestions.filter(
              (question) => question.id,
            );
            setStudentQuestions(tempQuestion);

            tempQuestion = tempSupervisorQuestions.filter(
              (question) => question.id,
            );
            setSupervisorQuestions(tempQuestion);
          })
          .catch((err) => toast.warn(errorMessage(err))),
      );
    } else {
      setStudentQuestions(studentQs.filter((question) => question.id));
      setSupervisorQuestions(supervisorQs.filter((question) => question.id));
    }

    questionsToUpdate.forEach((question) => {
      promises.push(
        updateEvaluationQuestion
          .mutateAsync(question)
          .catch((err) => toast.warn(errorMessage(err))),
      );
    });

    questionsToDelete.forEach((question) => {
      promises.push(
        deleteEvaluationQuestions
          .mutateAsync(question.id)
          .catch((err) => toast.warn(errorMessage(err))),
      );
    });

    await Promise.allSettled(promises);
    queryClient.invalidateQueries({ queryKey: ["FetchEvaluationQuestions", activePeriodId] });
    queryClient.invalidateQueries({ queryKey: ["FetchRatingSummary"] });

    if (questionsToDelete.length > 0) {
      setQuestionsToDelete([]);
    }
  };

  return {
    studentQuestions,
    setStudentQuestions,
    supervisorQuestions,
    setSupervisorQuestions,
    questionsToDelete,
    setQuestionsToDelete,
    handleSaveEvaluationQuestions,
    evaluationQuestionsData,
  };
}
